// Pack-spec editor (BUILD_SPEC.md §5.2) — the nested 1:1 resource on a "pack"
// product: which bulk product it contains, qty per pack, and packaging
// consumable lines (e.g. a bag or carton) — shared shape with the packconv
// module (packages/core/src/schemas/common.ts's `packSpecPackagingLineSchema`).
// `PUT /config/products/:id/pack-spec` is an idempotent upsert (services/api/
// src/modules/config/products.ts), so this one form covers both "make this a
// pack SKU" and "edit its existing pack spec". Plain `useState` (not
// `@mantine/form`) for the dynamic packaging-line rows, matching
// features/silo-counts/components/CalibrationForm.tsx's precedent for the same
// kind of nested-array editing.
import { useEffect, useState } from 'react';
import { Alert, Button, Group, Loader, Modal, NumberInput, Select, Stack, Table, Text, Tooltip } from '@mantine/core';
import { IconAlertTriangle, IconTrash } from '@tabler/icons-react';
import type { UpsertPackSpecInput } from '@zebra/core';
import { ApiRequestError } from '../../../api/client';
import { parseNum } from '../../../lib/parseNum';
import { useIsAdmin } from '../permissions';
import { isPackSpecMissing, usePackSpec, useUpsertPackSpec } from '../api';
import type { ProductDto } from '../types';

interface PackagingRow {
  productId: string;
  qty: number | string;
}

export function PackSpecModal({
  opened,
  onClose,
  packProduct,
  allProducts,
}: {
  opened: boolean;
  onClose: () => void;
  packProduct: ProductDto;
  allProducts: ProductDto[];
}) {
  const isAdmin = useIsAdmin();
  const packSpecQuery = usePackSpec(packProduct.id);
  const upsert = useUpsertPackSpec(packProduct.id);

  const [bulkProductId, setBulkProductId] = useState<string>('');
  const [qtyPerPack, setQtyPerPack] = useState<number | string>('');
  const [packaging, setPackaging] = useState<PackagingRow[]>([]);

  useEffect(() => {
    const spec = packSpecQuery.data;
    if (spec) {
      setBulkProductId(spec.bulkProductId);
      setQtyPerPack(Number(spec.qtyPerPack));
      setPackaging((spec.packaging ?? []).map((line) => ({ productId: line.productId, qty: Number(line.qty) })));
    } else if (isPackSpecMissing(packSpecQuery.error)) {
      setBulkProductId('');
      setQtyPerPack('');
      setPackaging([]);
    }
  }, [packSpecQuery.data, packSpecQuery.error]);

  const notFound = isPackSpecMissing(packSpecQuery.error);
  const realError = packSpecQuery.isError && !notFound;

  const bulkOptions = allProducts.filter((p) => p.id !== packProduct.id).map((p) => ({ value: p.id, label: `${p.sku} — ${p.name}` }));
  const packagingOptions = allProducts
    .filter((p) => p.id !== packProduct.id)
    .map((p) => ({ value: p.id, label: `${p.sku} — ${p.name}` }));

  const parsedQtyPerPack = parseNum(qtyPerPack);
  const bulkError = bulkProductId === '' ? 'Bulk product is required' : null;
  const qtyError = parsedQtyPerPack === null || parsedQtyPerPack <= 0 ? 'Qty per pack must be greater than zero' : null;
  const hasError =
    Boolean(bulkError || qtyError) ||
    packaging.some((line) => {
      const parsedQty = parseNum(line.qty);
      return !line.productId || parsedQty === null || parsedQty <= 0;
    });

  function updateRow(index: number, field: keyof PackagingRow, value: string | number) {
    setPackaging((rows) => rows.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
  }
  function removeRow(index: number) {
    setPackaging((rows) => rows.filter((_, i) => i !== index));
  }
  function addRow() {
    setPackaging((rows) => [...rows, { productId: '', qty: '' }]);
  }

  function handleSubmit() {
    if (hasError || parsedQtyPerPack === null) return;
    const payload: UpsertPackSpecInput = {
      bulkProductId,
      qtyPerPack: parsedQtyPerPack.toFixed(3),
      packaging:
        packaging.length > 0
          ? packaging.map((line) => ({ productId: line.productId, qty: (parseNum(line.qty) as number).toFixed(3) }))
          : null,
    };
    upsert.mutate(payload, { onSuccess: onClose });
  }

  return (
    <Modal opened={opened} onClose={onClose} title={`Pack spec — ${packProduct.sku} ${packProduct.name}`} centered size="lg">
      {packSpecQuery.isLoading ? (
        <Loader size="sm" />
      ) : realError ? (
        <Alert color="red" icon={<IconAlertTriangle size={18} />}>
          Failed to load pack spec.
        </Alert>
      ) : (
        <Stack gap="sm">
          {notFound && (
            <Text size="sm" c="dimmed">
              No pack spec configured yet for this product — fill this in to make it a pack SKU.
            </Text>
          )}
          <Select
            label="Bulk product"
            description="The base product this pack contains (e.g. bulk pellets for a bagged SKU)"
            data={bulkOptions}
            searchable
            required
            value={bulkProductId || null}
            onChange={(v) => setBulkProductId(v ?? '')}
            error={bulkError}
            disabled={!isAdmin}
          />
          <NumberInput
            label="Qty per pack"
            description={`In the bulk product's base UOM`}
            min={0}
            decimalScale={3}
            value={qtyPerPack}
            onChange={setQtyPerPack}
            error={qtyError}
            disabled={!isAdmin}
          />

          <Text size="sm" fw={500} mt="xs">
            Packaging lines (optional)
          </Text>
          <Table.ScrollContainer minWidth={640}>
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Packaging product</Table.Th>
                  <Table.Th>Qty per pack</Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {packaging.map((row, index) => (
                  <Table.Tr key={index}>
                    <Table.Td>
                      <Select
                        data={packagingOptions}
                        searchable
                        value={row.productId || null}
                        onChange={(v) => updateRow(index, 'productId', v ?? '')}
                        disabled={!isAdmin}
                      />
                    </Table.Td>
                    <Table.Td>
                      <NumberInput
                        min={0}
                        decimalScale={3}
                        value={row.qty}
                        onChange={(v) => updateRow(index, 'qty', v)}
                        disabled={!isAdmin}
                      />
                    </Table.Td>
                    <Table.Td>
                      {isAdmin && (
                        <Tooltip label="Remove this packaging line">
                          <Button variant="subtle" color="red" size="compact-sm" aria-label="Remove packaging line" onClick={() => removeRow(index)}>
                            <IconTrash size={16} />
                          </Button>
                        </Tooltip>
                      )}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
          {isAdmin && (
            <Group>
              <Button variant="default" size="compact-sm" onClick={addRow}>
                Add packaging line
              </Button>
            </Group>
          )}

          {upsert.isError && (
            <Alert color="red" icon={<IconAlertTriangle size={18} />}>
              {upsert.error instanceof ApiRequestError ? upsert.error.message : 'Failed to save pack spec.'}
            </Alert>
          )}

          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={onClose}>
              {isAdmin ? 'Cancel' : 'Close'}
            </Button>
            {isAdmin && (
              <Button onClick={handleSubmit} loading={upsert.isPending}>
                Save pack spec
              </Button>
            )}
          </Group>
        </Stack>
      )}
    </Modal>
  );
}
